import React, { useEffect, useState, useContext } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthCallbackPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setUser } = useContext(AuthContext);
  const [status, setStatus] = useState('processing');
  const [error, setError] = useState(null);

  useEffect(() => {
    const handleCallback = async () => {
      const code = searchParams.get('code');
      const state = searchParams.get('state');
      const errorParam = searchParams.get('error');

      if (errorParam) {
        setStatus('error');
        setError(searchParams.get('error_description') || 'Authentication was cancelled');
        return;
      }

      if (!code || !state) {
        // Check if this is a Google OAuth redirect (from Emergent auth)
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.has('token')) {
          // This is handled by App.js, just redirect
          navigate('/');
          return;
        }
        setStatus('error');
        setError('Missing authentication parameters');
        return;
      }

      try {
        // Exchange code for user data via backend
        const response = await axios.get(`${API}/auth/twitter/callback`, {
          params: { code, state }
        });

        const { user, redirect_uri } = response.data;
        
        // Store user in context and localStorage
        setUser(user);
        localStorage.setItem('user', JSON.stringify(user));
        
        setStatus('success');
        
        // Redirect after a brief delay
        setTimeout(() => {
          navigate(redirect_uri || '/');
        }, 1500);

      } catch (err) {
        console.error('Auth callback error:', err);
        setStatus('error');
        setError(err.response?.data?.detail || 'Authentication failed');
      }
    };

    handleCallback();
  }, [searchParams, navigate, setUser]);

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center" data-testid="auth-callback">
      <div className="bg-white p-8 rounded-xl shadow-lg text-center max-w-md w-full">
        {status === 'processing' && (
          <>
            <Loader2 className="h-16 w-16 text-[#007AFF] mx-auto mb-4 animate-spin" />
            <h2 className="text-2xl font-bold text-[#001f3f] mb-2">Signing you in...</h2>
            <p className="text-slate-600">Please wait while we complete your login.</p>
          </>
        )}
        
        {status === 'success' && (
          <>
            <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-[#001f3f] mb-2">Welcome!</h2>
            <p className="text-slate-600">Login successful. Redirecting you now...</p>
          </>
        )}
        
        {status === 'error' && (
          <>
            <XCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-[#001f3f] mb-2">Login Failed</h2>
            <p className="text-slate-600 mb-4">{error}</p>
            <button
              onClick={() => navigate('/')}
              className="bg-[#001f3f] text-white px-6 py-2 rounded-full font-bold hover:bg-[#003366] transition-colors"
            >
              Return Home
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default AuthCallbackPage;
